import { useEffect } from 'react';
import { useStore } from '../store/useStore';

export default function Dashboard() {
  const fetchDashboardSummary = useStore((state) => state.fetchDashboardSummary);
  const fetchServers = useStore((state) => state.fetchServers);
  const summary = useStore((state) => state.entitlement.summary);
  const servers = useStore((state) => state.server.servers);

  useEffect(() => {
    fetchDashboardSummary();
    fetchServers();
    const interval = setInterval(() => { fetchDashboardSummary(); fetchServers(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardSummary, fetchServers]);

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-xl font-semibold text-text-primary">Dashboard</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="card p-5">
          <p className="text-sm text-text-secondary mb-1">Customers</p>
          <p className="text-3xl font-bold text-text-primary" data-testid="summary-customers">
            {summary?.total_customers ?? '-'}
          </p>
        </div>

        <div className="card p-5">
          <p className="text-sm text-text-secondary mb-1">Active Agents</p>
          <p className="text-3xl font-bold text-text-primary" data-testid="summary-agents">
            {summary?.active_agents ?? '-'}
          </p>
        </div>

        <div className="card p-5">
          <p className="text-sm text-text-secondary mb-1">Total Seats</p>
          <p className="text-3xl font-bold text-text-primary" data-testid="summary-total-seats">
            {summary?.total_seats ?? '-'}
          </p>
        </div>

        <div className="card p-5">
          <p className="text-sm text-text-secondary mb-1">Used Seats</p>
          <p className="text-3xl font-bold text-primary" data-testid="summary-used-seats">
            {summary?.used_seats ?? '-'}
          </p>
          {summary && summary.total_seats > 0 && (
            <p className="text-xs text-text-secondary mt-1">
              {summary.free_seats} free
            </p>
          )}
        </div>

        <div className="card p-5">
          <p className="text-sm text-text-secondary mb-1">Active Alarms</p>
          <p className={`text-3xl font-bold ${summary && summary.active_alarms > 0 ? 'text-status-red' : 'text-status-green'}`} data-testid="summary-alarms">
            {summary?.active_alarms ?? '-'}
          </p>
        </div>

        <div className="card p-5">
          <p className="text-sm text-text-secondary mb-1">Registered Servers</p>
          <p className="text-3xl font-bold text-text-primary" data-testid="summary-servers">
            {servers.length}
          </p>
          {servers.length > 0 && (
            <p className="text-xs text-text-secondary mt-1">
              {servers.filter((s) => s.status === 'online').length} online
            </p>
          )}
        </div>
      </div>

      {/* Per-product breakdown */}
      {summary && summary.by_product.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h2 className="text-lg font-semibold text-text-primary">Seats by Product</h2>
          </div>

          <div className="grid grid-cols-[1fr_80px_80px_80px_200px] gap-3 items-center p-3 bg-dark-bg border-b border-white/10 text-xs font-medium text-text-secondary uppercase tracking-wider">
            <span>Product</span>
            <span className="text-right">Total</span>
            <span className="text-right">Used</span>
            <span className="text-right">Free</span>
            <span>Utilization</span>
          </div>

          {summary.by_product.map((product) => {
            const freeSeats = product.total_seats - product.used_seats;
            const utilPercent = product.total_seats > 0
              ? Math.round((product.used_seats / product.total_seats) * 100)
              : 0;

            return (
              <div
                key={product.product_id}
                className="grid grid-cols-[1fr_80px_80px_80px_200px] gap-3 items-center p-3 border-b border-white/5"
              >
                <span className="text-sm text-text-primary">{product.product_name}</span>
                <span className="text-sm text-text-primary text-right">{product.total_seats}</span>
                <span className="text-sm text-text-primary text-right">{product.used_seats}</span>
                <span className={`text-sm text-right ${freeSeats === 0 && product.total_seats > 0 ? 'text-status-red font-medium' : 'text-text-primary'}`}>
                  {freeSeats}
                </span>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-dark-bg rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        utilPercent < 50 ? 'bg-status-green' :
                        utilPercent < 80 ? 'bg-status-yellow' : 'bg-status-red'
                      }`}
                      style={{ width: `${Math.min(utilPercent, 100)}%` }}
                    />
                  </div>
                  <span className="w-10 text-right text-xs text-mono text-text-secondary">{utilPercent}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Per-customer breakdown */}
      {summary && summary.by_customer.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h2 className="text-lg font-semibold text-text-primary">Allocations by Customer</h2>
          </div>

          <div className="grid grid-cols-[1fr_1fr_100px_80px_80px_80px_200px] gap-3 items-center p-3 bg-dark-bg border-b border-white/10 text-xs font-medium text-text-secondary uppercase tracking-wider">
            <span>Customer</span>
            <span>Display Name</span>
            <span className="text-right">Entitlements</span>
            <span className="text-right">Total</span>
            <span className="text-right">Used</span>
            <span className="text-right">Agents</span>
            <span>Utilization</span>
          </div>

          {summary.by_customer.map((cust) => {
            const utilPercent = cust.total_seats > 0
              ? Math.round((cust.used_seats / cust.total_seats) * 100)
              : 0;

            return (
              <div
                key={cust.customer_id}
                className="grid grid-cols-[1fr_1fr_100px_80px_80px_80px_200px] gap-3 items-center p-3 border-b border-white/5"
              >
                <span className="text-sm text-text-primary font-medium">{cust.username}</span>
                <span className="text-sm text-text-secondary">{cust.display_name || '-'}</span>
                <span className="text-sm text-text-primary text-right">{cust.entitlement_count}</span>
                <span className="text-sm text-text-primary text-right">{cust.total_seats}</span>
                <span className="text-sm text-text-primary text-right">{cust.used_seats}</span>
                <span className="text-sm text-text-primary text-right">{cust.agent_count}</span>
                <div className="flex items-center gap-2">
                  {cust.total_seats > 0 ? (
                    <>
                      <div className="flex-1 h-2 bg-dark-bg rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            utilPercent < 50 ? 'bg-status-green' :
                            utilPercent < 80 ? 'bg-status-yellow' : 'bg-status-red'
                          }`}
                          style={{ width: `${Math.min(utilPercent, 100)}%` }}
                        />
                      </div>
                      <span className="w-10 text-right text-xs text-mono text-text-secondary">{utilPercent}%</span>
                    </>
                  ) : (
                    <span className="text-xs text-text-secondary">No seats</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {summary && summary.by_customer.length === 0 && summary.by_product.every((p) => p.total_seats === 0) && (
        <div className="card p-8 text-center">
          <p className="text-text-secondary">No license entitlements configured yet.</p>
          <p className="text-sm text-dimmed mt-1">Create customers and assign entitlements to see data here.</p>
        </div>
      )}
    </div>
  );
}
